import axios, { AxiosError, AxiosInstance, type AxiosRequestConfig } from 'axios';

import { addSentryBreadcrumb, captureError, track } from '../observability/sentry';
import { getCsrfToken, notifySessionInvalidated } from './sessionManager';

const AUTH_401_EXCLUDED_ROUTES = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/google',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/logout-all',
  '/api/auth/me',
];
const API_5XX_DEDUPE_TTL_MS = 5000;
const REQUEST_ID_STORAGE_KEY = 'ecovoz:request-id';
const recentServerErrors = new Map<string, number>();
let cachedRequestId: string | null = null;

const shouldIgnoreUnauthorized = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return AUTH_401_EXCLUDED_ROUTES.some((route) => url.includes(route));
};

const shouldTrackServerError = (fingerprint: string): boolean => {
  const now = Date.now();
  const expiresAt = recentServerErrors.get(fingerprint);

  if (expiresAt && expiresAt > now) {
    return false;
  }

  recentServerErrors.set(fingerprint, now + API_5XX_DEDUPE_TTL_MS);
  return true;
};

const hasSessionStorage = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
};

const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const getStoredRequestId = (): string | null => {
  if (cachedRequestId) {
    return cachedRequestId;
  }

  if (!hasSessionStorage()) {
    return null;
  }

  const stored = window.sessionStorage.getItem(REQUEST_ID_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  cachedRequestId = stored;
  return stored;
};

const setStoredRequestId = (requestId: string): void => {
  cachedRequestId = requestId;
  if (!hasSessionStorage()) {
    return;
  }

  window.sessionStorage.setItem(REQUEST_ID_STORAGE_KEY, requestId);
};

const getOrCreateRequestId = (): string => {
  const existing = getStoredRequestId();
  if (existing) {
    return existing;
  }

  const requestId = generateRequestId();
  setStoredRequestId(requestId);
  return requestId;
};

const readRequestIdHeader = (headers: unknown): string | null => {
  if (!headers) {
    return null;
  }

  if (typeof headers === 'object' && typeof (headers as { get?: unknown }).get === 'function') {
    const value = (headers as { get: (name: string) => unknown }).get('x-request-id');
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  const rawHeaders = headers as Record<string, unknown>;
  const candidates = [
    rawHeaders['x-request-id'],
    rawHeaders['X-Request-Id'],
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return null;
};

const setRequestIdHeader = (config: AxiosRequestConfig, requestId: string): void => {
  if (config.headers && typeof (config.headers as { set?: unknown }).set === 'function') {
    (config.headers as { set: (name: string, value: string) => void }).set('x-request-id', requestId);
    return;
  }

  config.headers = {
    ...(config.headers as Record<string, unknown> | undefined),
    'x-request-id': requestId,
  };
};

const isMutatingMethod = (method: string | undefined): boolean => {
  if (!method) {
    return false;
  }

  const normalized = method.toUpperCase();
  return normalized === 'POST' || normalized === 'PUT' || normalized === 'PATCH' || normalized === 'DELETE';
};

const setCsrfHeader = (config: AxiosRequestConfig, csrfToken: string): void => {
  if (config.headers && typeof (config.headers as { set?: unknown }).set === 'function') {
    (config.headers as { set: (name: string, value: string) => void }).set('x-csrf-token', csrfToken);
    return;
  }

  config.headers = {
    ...(config.headers as Record<string, unknown> | undefined),
    'x-csrf-token': csrfToken,
  };
};

const normalizeApiBaseUrl = (value?: string): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  const withoutTrailingSlash = trimmed.replace(/\/+$/, '');
  return withoutTrailingSlash.endsWith('/api')
    ? withoutTrailingSlash.slice(0, -4)
    : withoutTrailingSlash;
};

const envApiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
const baseURL = envApiBaseUrl || (import.meta.env.DEV ? '/' : 'https://ecovoz-backend.onrender.com');

const envTimeout = Number(import.meta.env.VITE_API_TIMEOUT);
const apiTimeout = Number.isFinite(envTimeout) && envTimeout > 0 ? envTimeout : 10000;

const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const existingRequestId = readRequestIdHeader(config.headers);
  if (!existingRequestId) {
    setRequestIdHeader(config, getOrCreateRequestId());
  }

  if (isMutatingMethod(config.method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      setCsrfHeader(config, csrfToken);
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url;
    const sentRequestId = readRequestIdHeader(error.config?.headers);
    const requestIdHeader = error.response?.headers?.['x-request-id'];
    const responseRequestId = Array.isArray(requestIdHeader)
      ? requestIdHeader[0]
      : requestIdHeader;
    const requestId = responseRequestId || sentRequestId;

    addSentryBreadcrumb('http', 'api_error', {
      status,
      requestUrl,
      requestId,
      sentRequestId,
      responseRequestId,
    });

    if (status === 401 && !shouldIgnoreUnauthorized(requestUrl)) {
      notifySessionInvalidated();
    }

    if (status === 402 && window.location.pathname !== '/') {
      window.location.assign('/?paywall=true');
      return Promise.reject(error);
    }

    if (status && status >= 500) {
      const fingerprint = `${status}:${requestUrl ?? 'unknown'}:${requestId ?? 'none'}`;
      if (!shouldTrackServerError(fingerprint)) {
        return Promise.reject(error);
      }

      track(
        'api_5xx_error',
        {
          status,
          requestUrl,
          requestId,
          sentRequestId,
          responseRequestId,
        },
        {
          feature: 'api',
          status: String(status),
          ...(requestId ? { request_id: requestId } : {}),
        },
      );
      captureError(
        error,
        {
          stage: 'api.response',
          status,
          requestUrl,
          requestId,
          sentRequestId,
          responseRequestId,
        },
        {
          feature: 'api',
          ...(requestId ? { request_id: requestId } : {}),
        },
      );
    }

    return Promise.reject(error);
  },
);

export default api;
